import queryString from 'query-string'
import { useEffect, useMemo } from 'react'
import { useHistory, useLocation } from 'react-router-dom'

import { saveAgnosticUserToken } from '../../use-cases/saveAgnosticUserToken'
import { saveMagicLinkToken } from '../../use-cases/saveMagicLinkToken'

export type UrlTokenKind = 'magic-link' | 'agnostic' | 'none'

const SAVE_BY_KIND = {
  'magic-link': saveMagicLinkToken,
  agnostic: saveAgnosticUserToken,
} as const

/**
 * Ingests the `?token=` URL param under the declared identity, then strips it
 * from the URL. A route's `?token=` must be ingested by exactly one kind —
 * ingesting it under the wrong kind overwrites another session's token.
 */
export function useSaveUrlToken(kind?: UrlTokenKind) {
  const location = useLocation()
  const history = useHistory()
  const parsed = useMemo(() => queryString.parse(location.search), [location.search])
  const urlToken = typeof parsed.token === 'string' ? parsed.token : null

  // Saved during render (not in an effect) so children mounted in the same
  // pass already find the token in storage when their data-fetch effects run —
  // child effects fire before parent effects.
  useMemo(() => {
    if (kind && kind !== 'none' && urlToken) {
      SAVE_BY_KIND[kind](urlToken)
    }
  }, [kind, urlToken])

  useEffect(() => {
    if (kind === 'none' || !urlToken) {
      return
    }

    // The token is saved, so we can remove it from the URL
    history.replace({
      search: queryString.stringify({
        ...parsed,
        token: undefined,
      }),
    })
  }, [kind, urlToken, parsed, history])
}
